/**
 * Sondos journey summary → cart attributes (order Admin note fields).
 * Local-only while browsing; /cart/update.js only at safe points.
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'sondos_journey_v2';
  var WINDOW_MS = 30 * 60 * 1000;
  var MAX_PAGES = 8;
  var MAX_PRODUCTS = 8;
  var MAX_COLLECTIONS = 6;
  var MAX_SEARCHES = 4;
  var MAX_ATTR = 240;
  var IDLE_SYNC_MS = 1800;
  var DEBOUNCE_MS = 400;

  var SECTION_MAP = [
    { sel: '.sk-hero', name: 'hero' },
    { sel: '.sts', name: 'trust' },
    { sel: '.sbg--categories', name: 'categories' },
    { sel: '.sbg:not(.sbg--categories)', name: 'brands' },
    { sel: '.arb', name: 'arb' },
    { sel: '[id*="bestsellers"] .sondos-featured-products, #shopify-section-bestsellers .sondos-featured-products', name: 'bestsellers' },
    { sel: '.sondos-featured-products', name: 'featured' },
    { sel: '.sondos-concerns', name: 'concerns' },
    { sel: '.sondos-categories', name: 'category-rail' },
    { sel: '.sondos-routine', name: 'routine' },
    { sel: '.srev', name: 'reviews' },
    { sel: '.sondos-trust', name: 'trust-social' },
    { sel: '.template-collection .collection', name: 'collection' },
    { sel: '.template-product .product', name: 'product' }
  ];

  var state = null;
  var syncTimer = null;
  var idleTimer = null;
  var syncing = false;
  var checkoutArmed = false;
  var checkoutBypass = false;
  var observed = typeof WeakSet !== 'undefined' ? new WeakSet() : null;
  var ATC_SOURCES = { 'product-form': true, arb: true };

  function now() {
    return Date.now();
  }

  function clip(str, max) {
    str = String(str == null ? '' : str);
    if (str.length <= max) return str;
    return str.slice(0, max - 1) + '…';
  }

  function pushUniqueTail(list, value, max) {
    if (!value) return list;
    var next = (list || []).filter(function (x) {
      return x !== value;
    });
    next.push(value);
    if (next.length > max) next = next.slice(next.length - max);
    return next;
  }

  function pushUnique(list, value) {
    if (!value) return list || [];
    var next = list || [];
    if (next.indexOf(value) !== -1) return next;
    next.push(value);
    return next;
  }

  function readQuery() {
    try {
      return new URLSearchParams(location.search || '');
    } catch (e) {
      return null;
    }
  }

  function detectDevice() {
    try {
      if (window.matchMedia && window.matchMedia('(max-width: 749px)').matches) return 'mobile';
    } catch (e) {
      /* ignore */
    }
    return 'desktop';
  }

  function readMarket() {
    var el = document.body;
    var m = el && el.getAttribute('data-sji-market');
    return m ? String(m).toUpperCase() : '';
  }

  function emptyJourney(ts) {
    return {
      startedAt: ts,
      lastAt: ts,
      landingPath: '',
      utm: '',
      device: detectDevice(),
      market: readMarket(),
      pages: [],
      pagesCount: 0,
      products: [],
      collections: [],
      searches: [],
      sections: [],
      lastAction: '',
      arbStarted: false,
      arbUsed: false,
      arbUses: 0,
      arbAnswers: '',
      arbRoutine: '',
      atc: 0,
      addSources: [],
      timeToAtcMin: '',
      promoSeen: false,
      waClicked: false,
      syncedHash: ''
    };
  }

  function normalize(data, ts) {
    var base = emptyJourney(ts);
    if (!data || typeof data !== 'object') return base;
    Object.keys(base).forEach(function (key) {
      if (typeof data[key] !== 'undefined') base[key] = data[key];
    });
    if (!base.device) base.device = detectDevice();
    if (!base.market) base.market = readMarket();
    if (!Array.isArray(base.pages)) base.pages = [];
    if (!Array.isArray(base.products)) base.products = [];
    if (!Array.isArray(base.collections)) base.collections = [];
    if (!Array.isArray(base.searches)) base.searches = [];
    if (!Array.isArray(base.sections)) base.sections = [];
    if (!Array.isArray(base.addSources)) base.addSources = [];
    base.lastAt = ts;
    return base;
  }

  function load() {
    var ts = now();
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) {
        /* migrate v1 once inside window */
        var legacy = localStorage.getItem('sondos_journey_v1');
        if (legacy) {
          var old = JSON.parse(legacy);
          if (old && old.lastAt && ts - old.lastAt <= WINDOW_MS) {
            return normalize(old, ts);
          }
        }
        return emptyJourney(ts);
      }
      var data = JSON.parse(raw);
      if (!data || typeof data !== 'object') return emptyJourney(ts);
      if (!data.lastAt || ts - data.lastAt > WINDOW_MS) return emptyJourney(ts);
      return normalize(data, ts);
    } catch (e) {
      return emptyJourney(ts);
    }
  }

  function save() {
    if (!state) return;
    state.lastAt = now();
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (e) {
      /* quota / private mode — keep in-memory only */
    }
  }

  function touch(action) {
    if (action) state.lastAction = clip(action, 80);
    save();
  }

  function captureLandingAndUtm() {
    var path = (location.pathname || '/').replace(/\/+$/, '') || '/';
    if (!state.landingPath) state.landingPath = path;

    if (!state.utm) {
      var q = readQuery();
      if (q) {
        var source = q.get('utm_source') || '';
        if (!source && q.get('fbclid')) source = 'fb';
        var medium = q.get('utm_medium') || '';
        var campaign = q.get('utm_campaign') || q.get('campaign_id') || '';
        var parts = [source, medium, campaign].filter(Boolean);
        if (parts.length) state.utm = clip(parts.join('|'), MAX_ATTR);
      }
    }

    if (!state.device) state.device = detectDevice();
    if (!state.market) state.market = readMarket();
  }

  function buildPayload() {
    var mins = Math.max(0, Math.round((state.lastAt - state.startedAt) / 60000));
    return {
      visit_minutes: String(mins),
      landing_path: clip(state.landingPath || '', MAX_ATTR),
      utm: clip(state.utm || '', MAX_ATTR),
      device: clip(state.device || '', 32),
      market: clip(state.market || '', 8),
      pages_viewed: clip((state.pages || []).join(' › '), MAX_ATTR),
      pages_count: String(state.pagesCount || 0),
      products_viewed: clip((state.products || []).join(' › '), MAX_ATTR),
      collections_viewed: clip((state.collections || []).join(' › '), MAX_ATTR),
      search_q: clip((state.searches || []).join(' › '), MAX_ATTR),
      sections_seen: clip((state.sections || []).join(', '), MAX_ATTR),
      last_action: clip(state.lastAction || '', MAX_ATTR),
      arb_started: state.arbStarted ? 'نعم' : 'لا',
      arb_used: state.arbUsed ? 'نعم' : 'لا',
      arb_uses: String(state.arbUses || 0),
      arb_answers: clip(state.arbAnswers || '', MAX_ATTR),
      arb_routine: clip(state.arbRoutine || '', MAX_ATTR),
      added_to_cart: String(state.atc || 0),
      add_source: clip((state.addSources || []).join(', '), MAX_ATTR),
      time_to_atc_min: state.timeToAtcMin === '' || state.timeToAtcMin == null ? '' : String(state.timeToAtcMin),
      promo_seen: state.promoSeen ? 'نعم' : 'لا',
      wa_clicked: state.waClicked ? 'نعم' : 'لا'
    };
  }

  function payloadHash(payload) {
    try {
      return JSON.stringify(payload);
    } catch (e) {
      return String(Math.random());
    }
  }

  function updateUrl() {
    return (window.routes && window.routes.cart_update_url) || '/cart/update.js';
  }

  function syncToCart(force) {
    if (syncing) return Promise.resolve(false);
    var payload = buildPayload();
    var hash = payloadHash(payload);
    if (!force && hash === state.syncedHash) return Promise.resolve(false);

    syncing = true;
    return fetch(updateUrl(), {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({ attributes: payload })
    })
      .then(function (res) {
        if (!res.ok) throw new Error('cart update ' + res.status);
        state.syncedHash = hash;
        save();
        return true;
      })
      .catch(function () {
        return false;
      })
      .then(function (ok) {
        syncing = false;
        return ok;
      });
  }

  function scheduleSync(delay) {
    if (syncTimer) clearTimeout(syncTimer);
    syncTimer = setTimeout(function () {
      syncTimer = null;
      syncToCart(false);
    }, typeof delay === 'number' ? delay : DEBOUNCE_MS);
  }

  function scheduleIdleSync() {
    if (idleTimer) clearTimeout(idleTimer);
    idleTimer = setTimeout(function () {
      idleTimer = null;
      scheduleSync(0);
    }, IDLE_SYNC_MS);
  }

  function decodePart(part) {
    try {
      return decodeURIComponent(part);
    } catch (e) {
      return part;
    }
  }

  function recordPage() {
    var path = (location.pathname || '/').replace(/\/+$/, '') || '/';
    state.pagesCount = (state.pagesCount || 0) + 1;
    state.pages = pushUniqueTail(state.pages, path, MAX_PAGES);
    touch('view:' + path);
  }

  function recordProduct() {
    var m = (location.pathname || '').match(/\/products\/([^/?#]+)/);
    if (!m) return;
    var handle = decodePart(m[1]);
    state.products = pushUniqueTail(state.products, handle, MAX_PRODUCTS);
    touch('product:' + handle);
  }

  function recordCollection() {
    var m = (location.pathname || '').match(/\/collections\/([^/?#]+)/);
    if (!m) return;
    var handle = decodePart(m[1]);
    if (handle === 'all') return;
    state.collections = pushUniqueTail(state.collections, handle, MAX_COLLECTIONS);
  }

  function recordSearch() {
    if ((location.pathname || '').indexOf('/search') !== 0) return;
    var q = readQuery();
    if (!q) return;
    var term = (q.get('q') || q.get('query') || '').trim();
    if (!term) return;
    state.searches = pushUniqueTail(state.searches, clip(term, 40), MAX_SEARCHES);
    touch('search:' + clip(term, 40));
  }

  function markSection(name) {
    if (!name || state.sections.indexOf(name) !== -1) return;
    state.sections.push(name);
    save();
  }

  function observeSections() {
    if (!('IntersectionObserver' in window)) {
      SECTION_MAP.forEach(function (item) {
        if (document.querySelector(item.sel)) markSection(item.name);
      });
      return;
    }

    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var name = entry.target.getAttribute('data-sji-section');
          if (name) markSection(name);
          io.unobserve(entry.target);
        });
      },
      { root: null, rootMargin: '0px', threshold: 0.2 }
    );

    SECTION_MAP.forEach(function (item) {
      var nodes = document.querySelectorAll(item.sel);
      for (var i = 0; i < nodes.length; i++) {
        var el = nodes[i];
        if (observed) {
          if (observed.has(el)) continue;
          observed.add(el);
        } else if (el.getAttribute('data-sji-section')) {
          continue;
        }
        el.setAttribute('data-sji-section', item.name);
        io.observe(el);
      }
    });
  }

  function watchPromo() {
    var promo = document.getElementById('snd-tw');
    if (!promo || typeof MutationObserver === 'undefined') return;

    function check() {
      if (promo.classList.contains('is-open') && !state.promoSeen) {
        state.promoSeen = true;
        touch('promo_seen');
      }
    }

    check();
    var mo = new MutationObserver(check);
    mo.observe(promo, { attributes: true, attributeFilter: ['class'] });
  }

  function mapAddSource(source) {
    if (source === 'arb') return 'arb';
    if (source === 'product-form') {
      if (document.body && document.body.classList.contains('template-product')) return 'pdp';
      return 'quick-add';
    }
    return source || '';
  }

  function isCheckoutControl(el) {
    if (!el || !el.closest) return null;
    var btn = el.closest('[name="checkout"], #checkout, #CartDrawer-Checkout, a[href*="/checkout"]');
    return btn || null;
  }

  function armCheckoutSync(trigger) {
    if (checkoutBypass || checkoutArmed || syncing) return false;
    var payload = buildPayload();
    if (payloadHash(payload) === state.syncedHash) return false;

    checkoutArmed = true;
    var form = trigger.form || (trigger.closest && trigger.closest('form'));
    var isLink = trigger.tagName === 'A' && trigger.href;

    syncToCart(true).finally(function () {
      checkoutArmed = false;
      checkoutBypass = true;
      window.setTimeout(function () {
        checkoutBypass = false;
      }, 2500);
      if (isLink) {
        window.location.href = trigger.href;
        return;
      }
      if (form) {
        if (typeof form.requestSubmit === 'function') form.requestSubmit(trigger);
        else form.submit();
        return;
      }
      if (typeof trigger.click === 'function') trigger.click();
    });
    return true;
  }

  function onPointerDown(e) {
    var btn = isCheckoutControl(e.target);
    if (!btn) return;
    if (armCheckoutSync(btn)) {
      e.preventDefault();
      e.stopPropagation();
    }
  }

  function onSubmit(e) {
    var form = e.target;
    if (!form || form.tagName !== 'FORM') return;
    var submitter = e.submitter || form.querySelector('[name="checkout"], #checkout, #CartDrawer-Checkout');
    if (!submitter || (submitter.name !== 'checkout' && submitter.id !== 'checkout' && submitter.id !== 'CartDrawer-Checkout')) {
      if (!form.querySelector('[name="checkout"]')) return;
      submitter = form.querySelector('[name="checkout"]');
    }
    if (!submitter) return;
    if (armCheckoutSync(submitter)) {
      e.preventDefault();
      e.stopPropagation();
    }
  }

  function onCartUpdated(e) {
    var source = e && e.detail && e.detail.source;
    if (!source || !ATC_SOURCES[source]) {
      scheduleIdleSync();
      return;
    }
    state.atc += 1;
    state.addSources = pushUnique(state.addSources, mapAddSource(source));
    if (state.timeToAtcMin === '' || state.timeToAtcMin == null) {
      state.timeToAtcMin = Math.max(0, Math.round((now() - state.startedAt) / 60000));
    }
    touch('add_to_cart:' + mapAddSource(source));
    scheduleIdleSync();
  }

  function onArbCompleted(e) {
    var detail = (e && e.detail) || {};
    state.arbStarted = true;
    state.arbUsed = true;
    state.arbUses = (state.arbUses || 0) + 1;
    if (detail.skinLabel || detail.concernLabel || detail.skin || detail.concern) {
      state.arbAnswers = clip(
        (detail.skinLabel || detail.skin || '') + ' · ' + (detail.concernLabel || detail.concern || ''),
        MAX_ATTR
      );
    }
    if (detail.products && detail.products.length) {
      state.arbRoutine = clip(detail.products.join(' › '), MAX_ATTR);
    }
    touch('arb_completed');
    save();

    if (typeof window.hj === 'function') {
      try {
        window.hj('event', 'arb_completed');
      } catch (err) {
        /* Hotjar optional */
      }
    }

    scheduleSync(0);
  }

  function onDocClick(e) {
    var t = e.target;
    if (!t || !t.closest) return;

    if (t.closest('[data-arb-start]')) {
      if (!state.arbStarted) {
        state.arbStarted = true;
        touch('arb_started');
      } else {
        touch('open_arb');
      }
      return;
    }

    if (
      t.closest(
        'a[href*="wa.me"], a[href*="api.whatsapp"], a[href*="whatsapp.com"], .arb__btn--whatsapp, [class*="njt-wa"], [id*="njt-wa"]'
      )
    ) {
      if (!state.waClicked) {
        state.waClicked = true;
        touch('wa_clicked');
      }
    }
  }

  function init() {
    state = load();
    captureLandingAndUtm();
    recordPage();
    recordProduct();
    recordCollection();
    recordSearch();
    observeSections();
    watchPromo();

    document.addEventListener('cart:updated', onCartUpdated, { passive: true });
    document.addEventListener('sondos:arb:completed', onArbCompleted, { passive: true });
    document.addEventListener(
      'sondos:section:hydrated',
      function () {
        observeSections();
      },
      { passive: true }
    );
    document.addEventListener('click', onDocClick, { passive: true });
    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('submit', onSubmit, true);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
